import { toast } from "react-toastify";
import { Popup } from "../../../SharedComponents/Popup";
import { SvgIcons } from "../../../SharedComponents/SvgIcons/SvgIcons";
import Dropdown from "../../../../UI/Dropdown/Dropdown";
import { ErrorText, WarningText, SuccessText } from "../../ui/StatusText";
import { FileUploadField } from "../../ui/FileUploadField";
import { usePanDetailsForm } from "../../services/hooks/usePanDetailsForm";
import { useBankDetailsForm } from "../../services/hooks/useBankDetailsForm";
import { getUserId } from "../../../../utils/auth";
import { fireEpicenterGtm } from "../../../../datalayers";
import {
  NO_PAN_REASONS,
  OTHER_NO_PAN_REASON,
  ACCOUNT_TYPES,
  PAN_STATUS_MESSAGES,
  BANK_STATUS_MESSAGES,
} from "../../services/utils/constants";
import styles from "./style.module.css";

export const EditPanAndBankDetailsModal = ({
  isOpen,
  onClose,
  panDetails = {},
  bankDetails = {},
  onSaveSuccess,
}) => {
  const userId = getUserId();
  const pan = usePanDetailsForm({ userId, currentDetails: panDetails, isOpen });
  const bank = useBankDetailsForm({ userId, currentDetails: bankDetails, isOpen });

  const saving = pan.loading.isSaving || bank.loading.isSaving;

  const isEditingExisting = Object.keys(panDetails).length > 0;

  const isFormComplete = isEditingExisting
    ? pan.derived.isDirty || bank.derived.isDirty
    : pan.derived.isFormComplete && bank.derived.isFormComplete;

  // On edit, only touch the side the mentor actually changed — otherwise an unrelated,
  // still-unmet requirement on the untouched side (e.g. Form 60 reset to empty on every
  // open) silently blocks a save that has nothing to do with it.
  const handleSave = async () => {
    const touchPan = !isEditingExisting || pan.derived.isDirty;
    const touchBank = !isEditingExisting || bank.derived.isDirty;

    const panErrors = touchPan ? pan.mutators.validate() : {};
    const bankErrors = touchBank ? bank.mutators.validate() : {};
    if (Object.keys(panErrors).length > 0 || Object.keys(bankErrors).length > 0) return;

    const skippedResult = { requestOk: false, verified: true, data: undefined };
    const [panResult, bankResult] = await Promise.all([
      touchPan ? pan.mutators.submit() : Promise.resolve(skippedResult),
      touchBank ? bank.mutators.submit() : Promise.resolve(skippedResult),
    ]);

    if ((panResult.requestOk || bankResult.requestOk) && onSaveSuccess) {
      onSaveSuccess({ panDetails: panResult.data, bankDetails: bankResult.data });
    }

    if (panResult.verified && bankResult.verified) {
      fireEpicenterGtm("kyc_submitted", ["", "profile"]);
      toast.success("Details saved");
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <Popup onClose={onClose} contentClassName={styles.modalContent} hideCloseButton>
      <div className={styles.stickyHeader}>
        <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Close">
          <SvgIcons name="cross_btn" width="20" height="20" style={{ color: "#999" }} />
        </button>
      </div>
      <div className={styles.formBody}>
        <span className={styles.title}>PAN details</span>

        <div className={styles.fieldGroup}>
          <label className={styles.label}>
            Do you have a PAN? <span>*</span>
          </label>
          <div className={styles.toggleRow}>
            <button
              type="button"
              disabled={saving}
              className={`${styles.toggleBtn} ${pan.state.hasPan === "yes" ? styles.toggleBtnActive : ""}`}
              onClick={() => pan.handlers.setHasPan("yes")}
            >
              Yes, I have a PAN
            </button>
            <button
              type="button"
              disabled={saving}
              className={`${styles.toggleBtn} ${pan.state.hasPan === "no" ? styles.toggleBtnActive : ""}`}
              onClick={() => pan.handlers.setHasPan("no")}
            >
              No
            </button>
          </div>
          {pan.state.errors.hasPan && <ErrorText>{pan.state.errors.hasPan}</ErrorText>}
          {pan.state.hasPan === "no" && (
            <div className={styles.hintRow}>
              <SvgIcons name="info_circle" width="14" height="14" className={styles.hintIcon} />
              <span className={styles.hintText}>
                As per regulations, 20% TDS applies if PAN is not provided
              </span>
            </div>
          )}
        </div>

        <div className={styles.divider} />

        {pan.state.hasPan === "yes" ? (
          <>
            <div className={styles.fieldGroup}>
              <label className={styles.label}>Is PAN linked with Aadhar?</label>
              <div className={styles.toggleRow}>
                <button
                  type="button"
                  disabled={saving}
                  className={`${styles.toggleBtn} ${pan.state.isAadharLinked === "yes" ? styles.toggleBtnActive : ""}`}
                  onClick={() => pan.handlers.setIsAadharLinked("yes")}
                >
                  Yes, it is linked
                </button>
                <button
                  type="button"
                  disabled={saving}
                  className={`${styles.toggleBtn} ${pan.state.isAadharLinked === "no" ? styles.toggleBtnActive : ""}`}
                  onClick={() => pan.handlers.setIsAadharLinked("no")}
                >
                  No
                </button>
              </div>
              {pan.state.isAadharLinked === "no" && (
                <div className={styles.hintRow}>
                  <SvgIcons name="info_circle" width="14" height="14" className={styles.hintIcon} />
                  <span className={styles.hintText}>
                    As per regulations, 20% TDS applies if PAN is not linked with Aadhaar. We
                    confirm this automatically once you save — no need to double check it here.
                  </span>
                </div>
              )}
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.label}>
                Full name as per PAN <span>*</span>
              </label>
              <input
                disabled={saving}
                className={`${styles.input} ${pan.state.errors.fullName ? styles.inputError : ""}`}
                value={pan.state.fullName}
                onChange={(e) => pan.handlers.setFullName(e.target.value)}
                placeholder="Enter your name as per PAN"
              />
              {pan.state.errors.fullName && <ErrorText>{pan.state.errors.fullName}</ErrorText>}
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.label}>
                PAN Number <span>*</span>
              </label>
              <input
                disabled={saving}
                className={`${styles.input} ${pan.state.errors.panNumber ? styles.inputError : ""}`}
                value={pan.state.panNumber}
                onChange={(e) => pan.handlers.setPanNumber(e.target.value)}
                placeholder="ABCDE9876F"
                maxLength={10}
              />
              {pan.state.errors.panNumber && <ErrorText>{pan.state.errors.panNumber}</ErrorText>}
              {pan.loading.isSaving && <span className={styles.verifyingText}>Verifying your PAN details...</span>}
              {!pan.loading.isSaving && pan.state.verificationStatus === "verified" && (
                <SuccessText>PAN verified successfully</SuccessText>
              )}
              {!pan.loading.isSaving && pan.state.verificationStatus === "failed" && (
                <ErrorText>{PAN_STATUS_MESSAGES[pan.state.verificationStatus]}</ErrorText>
              )}
              {!pan.loading.isSaving &&
                PAN_STATUS_MESSAGES[pan.state.verificationStatus] &&
                pan.state.verificationStatus !== "failed" && (
                  <WarningText>{PAN_STATUS_MESSAGES[pan.state.verificationStatus]}</WarningText>
                )}
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.label}>
                Date of Birth (as per PAN) <span>*</span>
              </label>
              <input
                type="date"
                required
                disabled={saving}
                className={`${styles.input} ${pan.state.errors.dob ? styles.inputError : ""}`}
                value={pan.state.dob}
                onChange={(e) => pan.handlers.setDob(e.target.value)}
                max={new Date().toISOString().split("T")[0]}
              />
              {pan.state.errors.dob && <ErrorText>{pan.state.errors.dob}</ErrorText>}
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.label}>
                Upload PAN <span>*</span>
              </label>
              <FileUploadField
                inputRef={pan.state.fileInputRef}
                file={pan.state.panFile}
                existingUrl={pan.state.existingPanDocumentUrl}
                existingName={pan.state.existingPanDocumentName}
                onView={() => pan.handlers.viewDocument("pan_copy")}
                onSelect={pan.handlers.handleFileSelect}
                accept=".jpg,.jpeg,.png,.pdf"
                hint="JPG, PNG or PDF"
                error={pan.state.errors.panFile}
                disabled={saving}
              />
              {pan.state.errors.panFile && <ErrorText>{pan.state.errors.panFile}</ErrorText>}
              {pan.state.apiError && (
                <div className={styles.apiErrorRow}>
                  <SvgIcons name="error_circle_icon" width="16" height="16" className={styles.apiErrorIcon} />
                  <span className={styles.apiErrorText}>{pan.state.apiError}</span>
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <div className={styles.fieldGroup}>
              <label className={styles.label}>
                Reason for not having PAN <span>*</span>
              </label>
              <button
                ref={pan.state.reasonTriggerRef}
                type="button"
                disabled={saving}
                className={`${styles.selectTrigger} ${pan.state.errors.reasonForNoPan ? styles.inputError : ""}`}
                onClick={pan.handlers.toggleReasonDropdown}
              >
                <span
                  className={`${styles.selectTriggerLabel} ${pan.state.reasonForNoPan ? styles.selectTriggerLabelFilled : ""
                    }`}
                >
                  {NO_PAN_REASONS.find((reason) => reason.value === pan.state.reasonForNoPan)?.label ||
                    "Select reason"}
                </span>
                <SvgIcons
                  name="chevron_down"
                  width={20}
                  height={20}
                  style={{
                    color: "#858585",
                    flexShrink: 0,
                    transform: pan.state.isReasonDropdownOpen ? "rotate(180deg)" : undefined,
                    transition: "transform 0.15s",
                  }}
                />
              </button>
              <Dropdown
                anchorRef={pan.state.reasonTriggerRef}
                open={pan.state.isReasonDropdownOpen}
                onClose={pan.handlers.closeReasonDropdown}
                className={styles.selectDropdownPanel}
              >
                <div className={styles.selectList}>
                  {NO_PAN_REASONS.map((reason) => (
                    <button
                      key={reason.value}
                      type="button"
                      className={`${styles.selectRow} ${reason.value === pan.state.reasonForNoPan ? styles.selectRowSelected : ""
                        }`}
                      onClick={() => pan.handlers.setReasonForNoPan(reason.value)}
                    >
                      {reason.label}
                    </button>
                  ))}
                </div>
              </Dropdown>
              {pan.state.errors.reasonForNoPan && <ErrorText>{pan.state.errors.reasonForNoPan}</ErrorText>}
              {pan.state.reasonForNoPan === OTHER_NO_PAN_REASON && (
                <>
                  <input
                    disabled={saving}
                    className={`${styles.input} ${pan.state.errors.otherReasonText ? styles.inputError : ""}`}
                    value={pan.state.otherReasonText}
                    onChange={(e) => pan.handlers.setOtherReasonText(e.target.value)}
                    placeholder="Please specify your reason"
                  />
                  {pan.state.errors.otherReasonText && <ErrorText>{pan.state.errors.otherReasonText}</ErrorText>}
                </>
              )}
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.label}>
                Upload Form 60 <span>*</span>
              </label>
              <FileUploadField
                inputRef={pan.state.form60InputRef}
                file={pan.state.form60File}
                existingUrl={pan.state.existingForm60DocumentUrl}
                existingName={pan.state.existingForm60DocumentName}
                onView={() => pan.handlers.viewDocument("form_60")}
                onSelect={pan.handlers.handleForm60Select}
                accept=".jpg,.jpeg,.png,.pdf"
                hint="Signed document · JPG, PNG or PDF"
                error={pan.state.errors.form60File}
                disabled={saving}
              />
              {pan.state.errors.form60File && <ErrorText>{pan.state.errors.form60File}</ErrorText>}
              {pan.state.apiError && (
                <div className={styles.apiErrorRow}>
                  <SvgIcons name="error_circle_icon" width="16" height="16" className={styles.apiErrorIcon} />
                  <span className={styles.apiErrorText}>{pan.state.apiError}</span>
                </div>
              )}
            </div>
          </>
        )}

        <div className={styles.sectionDivider} />

        <span className={styles.title}>Bank details</span>

        <div className={styles.fieldGroup}>
          <label className={styles.label}>
            Account holder name <span>*</span>
          </label>
          <input
            disabled={saving}
            className={`${styles.input} ${bank.state.errors.accountHolderName ? styles.inputError : ""}`}
            value={bank.state.accountHolderName}
            onChange={(e) => bank.handlers.setAccountHolderName(e.target.value)}
            placeholder="As per bank records"
          />
          {bank.state.errors.accountHolderName && <ErrorText>{bank.state.errors.accountHolderName}</ErrorText>}
        </div>

        <div className={styles.fieldGroup}>
          <label className={styles.label}>
            Mobile number <span>*</span>
          </label>
          <input
            disabled={saving}
            className={`${styles.input} ${bank.state.errors.mobileNumber ? styles.inputError : ""}`}
            value={bank.state.mobileNumber}
            onChange={(e) => bank.handlers.setMobileNumber(e.target.value)}
            placeholder="Enter 10-digit mobile number"
            inputMode="numeric"
            maxLength={10}
          />
          {bank.state.errors.mobileNumber && <ErrorText>{bank.state.errors.mobileNumber}</ErrorText>}
        </div>

        <div className={styles.fieldGroup}>
          <label className={styles.label}>
            IFSC code <span>*</span>
          </label>
          <input
            disabled={saving}
            className={`${styles.input} ${bank.state.errors.ifscCode || bank.derived.showIfscFormatError || bank.state.ifscStatus === "failed"
              ? styles.inputError
              : ""
              }`}
            value={bank.state.ifscCode}
            onChange={(e) => bank.handlers.setIfscCode(e.target.value)}
            placeholder="HDFC0001234"
            maxLength={11}
          />
          {bank.derived.showIfscFormatError && <ErrorText>Invalid IFSC code</ErrorText>}
          {!bank.derived.showIfscFormatError && bank.state.errors.ifscCode && (
            <ErrorText>{bank.state.errors.ifscCode}</ErrorText>
          )}
          {!bank.derived.showIfscFormatError && !bank.state.errors.ifscCode && bank.state.ifscStatus === "verifying" && (
            <span className={styles.verifyingText}>Verifying IFSC code...</span>
          )}
          {!bank.derived.showIfscFormatError && !bank.state.errors.ifscCode && bank.state.ifscStatus === "success" && (
            <SuccessText>IFSC verified successfully</SuccessText>
          )}
          {!bank.derived.showIfscFormatError && !bank.state.errors.ifscCode && bank.state.ifscStatus === "failed" && (
            <ErrorText>{bank.state.ifscError || "Invalid IFSC code"}</ErrorText>
          )}
        </div>

        <div className={styles.fieldGroup}>
          <label className={styles.label}>
            Bank name <span>*</span>
          </label>
          <input
            disabled
            readOnly
            className={styles.disabledInput}
            value={bank.state.bankName}
            placeholder="Please enter valid IFSC code"
          />
        </div>

        <div className={styles.fieldGroup}>
          <label className={styles.label}>
            Branch name <span>*</span>
          </label>
          <input
            disabled
            readOnly
            className={styles.disabledInput}
            value={bank.state.branchName}
            placeholder="Please enter valid IFSC code"
          />
        </div>

        <div className={styles.fieldGroup}>
          <label className={styles.label}>
            Account number <span>*</span>
          </label>
          <input
            disabled={saving}
            className={`${styles.input} ${bank.state.errors.accountNumber ? styles.inputError : ""}`}
            value={bank.state.accountNumber}
            onChange={(e) => bank.handlers.setAccountNumber(e.target.value)}
            placeholder="Enter account number"
            inputMode="numeric"
          />
          {bank.state.errors.accountNumber && <ErrorText>{bank.state.errors.accountNumber}</ErrorText>}
        </div>

        <div className={styles.fieldGroup}>
          <label className={styles.label}>
            Re-enter Account number <span>*</span>
          </label>
          <input
            disabled={saving}
            className={`${styles.input} ${bank.state.errors.confirmAccountNumber || bank.derived.accountNumbersMismatch ? styles.inputError : ""
              }`}
            value={bank.state.confirmAccountNumber}
            onChange={(e) => bank.handlers.setConfirmAccountNumber(e.target.value)}
            onPaste={(e) => e.preventDefault()}
            onCopy={(e) => e.preventDefault()}
            onCut={(e) => e.preventDefault()}
            onContextMenu={(e) => e.preventDefault()}
            placeholder="Re-enter account number"
            inputMode="numeric"
          />
          {bank.derived.accountNumbersMismatch && <ErrorText>Account numbers do not match</ErrorText>}
          {!bank.derived.accountNumbersMismatch && bank.state.errors.confirmAccountNumber && (
            <ErrorText>{bank.state.errors.confirmAccountNumber}</ErrorText>
          )}
          {bank.derived.accountNumbersMatch && <SuccessText>Account verified successfully</SuccessText>}
        </div>

        <div className={styles.fieldGroup}>
          <label className={styles.label}>
            Account type <span>*</span>
          </label>
          <div className={styles.toggleRow}>
            {ACCOUNT_TYPES.map((type) => (
              <button
                key={type.value}
                type="button"
                disabled={saving}
                className={`${styles.toggleBtn} ${bank.state.accountType === type.value ? styles.toggleBtnActive : ""}`}
                onClick={() => bank.handlers.setAccountType(type.value)}
              >
                {type.label}
              </button>
            ))}
          </div>
        </div>

        <div className={styles.fieldGroup}>
          <label className={styles.label}>
            Cancelled cheque or Passbook front page <span>*</span>
          </label>
          <FileUploadField
            inputRef={bank.state.chequeInputRef}
            file={bank.state.chequeFile}
            existingUrl={bank.state.existingBankProofUrl}
            existingName={bank.state.existingBankProofName}
            onView={bank.handlers.viewDocument}
            onSelect={bank.handlers.handleChequeFileSelect}
            accept=".jpg,.jpeg,.png,.pdf"
            hint="JPG, PNG or PDF"
            error={bank.state.errors.chequeFile}
            disabled={saving}
          />
          {bank.state.errors.chequeFile && <ErrorText>{bank.state.errors.chequeFile}</ErrorText>}
          {bank.loading.isSaving && <span className={styles.verifyingText}>Verifying your bank details...</span>}
          {!bank.loading.isSaving && bank.state.verificationStatus === "verified" && (
            <SuccessText>Bank details verified successfully</SuccessText>
          )}
          {!bank.loading.isSaving && bank.state.verificationStatus === "failed" && (
            <ErrorText>{BANK_STATUS_MESSAGES[bank.state.verificationStatus]}</ErrorText>
          )}
          {!bank.loading.isSaving &&
            BANK_STATUS_MESSAGES[bank.state.verificationStatus] &&
            bank.state.verificationStatus !== "failed" && (
              <WarningText>{BANK_STATUS_MESSAGES[bank.state.verificationStatus]}</WarningText>
            )}
          {bank.state.apiError && (
            <div className={styles.apiErrorRow}>
              <SvgIcons name="error_circle_icon" width="16" height="16" className={styles.apiErrorIcon} />
              <span className={styles.apiErrorText}>{bank.state.apiError}</span>
            </div>
          )}
        </div>
      </div>

      <div className={styles.stickyFooter}>
        <button
          className={styles.saveBtn}
          onClick={handleSave}
          disabled={saving || !isFormComplete}
        >
          {saving && <span className={styles.spinner} />}
          Save details
        </button>
      </div>
    </Popup>
  );
};
